/* =========================================================================
   Daily quest engine ("Today's Pilgrimage"), client side. Three fixed daily
   quests, evaluated LOCALLY off the existing telemetry beats so guests and
   offline players progress with zero server dependency; accounts additionally
   mirror progress to the server (usp_Quest_Record) fire-and-forget.

   How events reach quests: telemetry.ts's track() forwards EVERY event to
   questEvent() via the setTrackObserver seam (wired in main.ts). We never
   re-derive game logic — we subscribe to the same beats the game already emits:
     QuestionAnswered{category ∈ Scripture} → daily_scripture20
     DidYouKnowShown                        → daily_dyk1
     ArcadePlayed                           → daily_arcade1

   FeastVisited → daily_feast, GtkyAnswered → daily_gtky1, and
   InventoryGranted{type:'relic_token'} → daily_relic1 are all REMOVED
   (production/TODO.md + db/039_deactivate_dead_quests.sql, 2026-08-04):
   daily_feast/daily_gtky1 had no working way to ever complete them, and
   daily_relic1 (Eric: "which people don't understand") worked fine but read
   as confusing, and Eric chose to cut it rather than reword it.

   Progress persists at localStorage['catholics.quests.<ET-date>'] (older days
   pruned). Pure logic (topicOf, event→quest mapping, progress reducer) is
   unit-tested; storage uses the injectable-KVStore seam dailyBlessing.ts set.
   ========================================================================= */
import { track } from './telemetry';
import { etDateString } from './today';
import { getQuestsToday, recordQuest, isAuthenticated } from './api';
import { getLine } from './characters/registry';
import { triggerCharPop } from './characters/ui';
import type { KVStore } from './dailyBlessing';

/* ------------------------------ quest catalog ------------------------------ */

export interface QuestDef {
  key: string;
  title: string;
  descr: string;
  icon: string;
  targetCount: number;
}

/** The 3 active v1.1.0 dailies — keys/titles/targets/icons mirror db/014's seed
    (a CROSS-AGENT CONTRACT). Used offline / for guests / before the server
    catalog loads; the server catalog (when reachable) overrides titles/targets.
    daily_feast, daily_gtky1, daily_relic1 removed (see header + db/039); their
    journey stops (feast, gtky, relic) are likewise dropped from STOP_ORDER in
    journey.ts. */
export const BUNDLED_QUESTS: readonly QuestDef[] = [
  // `icon` is a brand IconName (ui/icons.ts); journey.ts renders it as SVG.
  { key: 'daily_scripture20', title: 'Scripture Scholar', descr: 'Answer 20 Scripture questions today.', icon: 'book', targetCount: 20 },
  { key: 'daily_dyk1', title: 'Did You Know?', descr: 'Learn one Did-You-Know fact.', icon: 'bulb', targetCount: 1 },
  { key: 'daily_arcade1', title: 'Arcade Break', descr: 'Play one arcade game.', icon: 'gamepad', targetCount: 1 },
];

/* --------------------------- pure category logic --------------------------- */

/** Categories that count as "Scripture" for daily_scripture20: the label "Bible"
    plus the seeded Bible-story categories (freeform per-question strings). */
const SCRIPTURE_KEYWORDS = ['bible', 'noah', 'jonah', 'abraham', 'moses', 'david', 'daniel', 'exodus', 'babel', 'elijah', 'joseph', 'job', 'isaiah'];

/** Coarse topic of a question category. Returns 'Scripture' for Bible + Bible
    stories, otherwise the original category unchanged. */
export function topicOf(category: string | undefined | null): string {
  if (!category) return '';
  const c = category.toLowerCase();
  return SCRIPTURE_KEYWORDS.some((k) => c.includes(k)) ? 'Scripture' : category;
}

/* ------------------------- pure event → quest mapping ------------------------- */

/** Map a telemetry event to the quest key + delta it advances, or null. Pure. */
export function questForEvent(
  eventKey: string,
  props?: Record<string, unknown>,
): { key: string; delta: number } | null {
  // Friendly Play (mode:'friendly') is education-only: an untimed study set must
  // not advance a daily quest, whose completion posts a real reward. Guarded here
  // — the single funnel every event passes through — rather than at each case.
  if (props?.mode === 'friendly') return null;
  switch (eventKey) {
    case 'QuestionAnswered':
      return topicOf(props?.category as string) === 'Scripture' ? { key: 'daily_scripture20', delta: 1 } : null;
    case 'DidYouKnowShown':
      return { key: 'daily_dyk1', delta: 1 };
    case 'ArcadePlayed':
      return { key: 'daily_arcade1', delta: 1 };
    // daily_feast (FeastVisited), daily_gtky1 (GtkyAnswered), and daily_relic1
    // (InventoryGranted relic_token) removed — see header + db/039.
    default:
      return null;
  }
}

/* --------------------------- pure progress reducer --------------------------- */

export type ProgressMap = Record<string, number>;

/** Advance one quest's progress, capped at its target. Reports whether THIS call
    crossed the target (first completion). Pure — never mutates the input map. */
export function questProgressReduce(
  defs: readonly QuestDef[],
  progress: ProgressMap,
  questKey: string,
  delta: number,
): { progress: ProgressMap; newProgress: number; completedNow: boolean } {
  const def = defs.find((d) => d.key === questKey);
  if (!def) return { progress, newProgress: progress[questKey] || 0, completedNow: false };
  const before = progress[questKey] || 0;
  if (before >= def.targetCount) return { progress, newProgress: before, completedNow: false }; // already done, capped
  const after = Math.min(def.targetCount, before + Math.max(0, delta));
  const completedNow = before < def.targetCount && after >= def.targetCount;
  return { progress: { ...progress, [questKey]: after }, newProgress: after, completedNow };
}

/* ------------------------------- storage seam ------------------------------- */

const KEY_PREFIX = 'catholics.quests.';

function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The ET period key ('YYYY-MM-DD') the quest log is bucketed by right now. */
export function currentPeriod(now: Date = new Date()): string {
  return etDateString(now);
}

function storageKey(period: string): string {
  return KEY_PREFIX + period;
}

export function readProgress(period: string, store: KVStore | null = deviceStore()): ProgressMap {
  try {
    if (!store) return {};
    const raw = store.getItem(storageKey(period));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as ProgressMap) : {};
  } catch {
    return {};
  }
}

function writeProgress(period: string, map: ProgressMap, store: KVStore | null): void {
  try {
    if (store) store.setItem(storageKey(period), JSON.stringify(map));
  } catch {
    /* private mode — quest progress simply won't persist */
  }
}

/** Remove any 'catholics.quests.<date>' keys other than today's (pure over the
    injected store; returns the removed keys for testing). */
export function pruneOldQuestKeys(keepPeriod: string, store: KVStore | null = deviceStore()): string[] {
  const removed: string[] = [];
  try {
    if (!store || typeof (store as Storage).length !== 'number') return removed;
    const s = store as Storage;
    const doomed: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k && k.startsWith(KEY_PREFIX) && k !== storageKey(keepPeriod)) doomed.push(k);
    }
    for (const k of doomed) {
      s.removeItem(k);
      removed.push(k);
    }
  } catch {
    /* ignore */
  }
  return removed;
}

/* ------------------------------- live engine ------------------------------- */

let catalog: QuestDef[] = BUNDLED_QUESTS.slice();
let questChangeObserver: (() => void) | null = null;

/** Journey Director (and any UI) subscribe here to re-render when quest state
    changes. */
export function setQuestChangeObserver(fn: (() => void) | null): void {
  questChangeObserver = fn;
}

/* ---- raw telemetry fan-out (the ONE-observer seam, shared) ----
   telemetry.ts supports a single track() observer, and quests.ts owns it
   (setTrackObserver(questEvent) in main.ts). Rather than a second observer,
   subscribers (e.g. the Pilgrimage gate engine) register here and receive EVERY
   event that flows through questEvent — fired before quest mapping, so gate
   triggers that aren't quest triggers (SessionCompleted, GalleryPurchased,
   CollectibleEarned, category-filtered QuestionAnswered) still reach them. */
type RawEventObserver = (eventKey: string, props?: Record<string, unknown>) => void;
const rawObservers: RawEventObserver[] = [];

/** Subscribe to the raw track() event stream. Returns an unsubscribe fn. */
export function onQuestEvent(cb: RawEventObserver): () => void {
  rawObservers.push(cb);
  return () => {
    const i = rawObservers.indexOf(cb);
    if (i >= 0) rawObservers.splice(i, 1);
  };
}

/** The catalog in effect (server-merged when available, else bundled). */
export function getQuestCatalog(): QuestDef[] {
  return catalog;
}

export interface QuestLogRow extends QuestDef {
  progress: number;
  completed: boolean;
}

/** The full 6-row quest log for rendering, merging the catalog with today's
    local progress. */
export function getQuestLog(now: Date = new Date(), store: KVStore | null = deviceStore()): QuestLogRow[] {
  const progress = readProgress(currentPeriod(now), store);
  return catalog.map((d) => {
    const p = Math.min(d.targetCount, progress[d.key] || 0);
    return { ...d, progress: p, completed: p >= d.targetCount };
  });
}

/** True when all 6 dailies are complete for today. */
export function allQuestsComplete(now: Date = new Date(), store: KVStore | null = deviceStore()): boolean {
  return getQuestLog(now, store).every((r) => r.completed);
}

/** THE central telemetry shim entry (registered as the track() observer). Maps
    the event to a quest, advances local progress, persists, celebrates first
    completion, and (authed) mirrors to the server. Never throws. */
export function questEvent(eventKey: string, props?: Record<string, unknown>): void {
  // Fan the raw event out to any subscribers (pilgrimage gate engine, …) FIRST,
  // before the quest-mapping filter, so non-quest triggers still reach them.
  for (const obs of rawObservers) {
    try {
      obs(eventKey, props);
    } catch {
      /* a subscriber must never break quest recording */
    }
  }
  const hit = questForEvent(eventKey, props);
  if (!hit) return;
  const store = deviceStore();
  const period = currentPeriod();
  const current = readProgress(period, store);
  const { progress, newProgress, completedNow } = questProgressReduce(catalog, current, hit.key, hit.delta);
  if (progress === current) return; // no change (unknown key or already capped)
  writeProgress(period, progress, store);

  // Accounts: mirror the delta to the server (idempotent by count-bucket).
  if (isAuthenticated()) {
    void recordQuest({
      questKey: hit.key,
      periodKey: period,
      delta: hit.delta,
      idempotencyKey: `${hit.key}:${period}:${newProgress}`,
    });
  }

  if (completedNow) {
    track('QuestCompleted', { key: hit.key });
    try {
      triggerCharPop(getLine('theo', 'quest-complete'));
    } catch {
      /* no DOM (tests) — skip the pop */
    }
  }
  if (questChangeObserver) {
    try {
      questChangeObserver();
    } catch {
      /* observer must not break quest recording */
    }
  }
}

/** Boot: prune stale day keys, register the telemetry shim, then (async) fetch
    the server catalog + merge any server-side progress into local state. Safe to
    call once at startup; the game is fully playable before the fetch resolves. */
export async function initQuests(now: Date = new Date()): Promise<void> {
  const store = deviceStore();
  const period = currentPeriod(now);
  pruneOldQuestKeys(period, store);

  const r = await getQuestsToday(period);
  if (r.ok && Array.isArray(r.data) && r.data.length > 0) {
    catalog = r.data.map((row) => ({
      key: row.key,
      title: row.title,
      descr: row.descr ?? '',
      icon: row.icon ?? 'star',
      targetCount: row.targetCount,
    }));
    // Seed local progress from the server (max with local so a just-earned local
    // beat is never lost), so returning accounts see their real standing.
    const local = readProgress(period, store);
    let changed = false;
    for (const row of r.data) {
      const serverP = row.completedUtc ? row.targetCount : row.progress || 0;
      const merged = Math.max(local[row.key] || 0, serverP);
      if (merged !== (local[row.key] || 0)) {
        local[row.key] = merged;
        changed = true;
      }
    }
    if (changed) writeProgress(period, local, store);
  }
  if (questChangeObserver) {
    try {
      questChangeObserver();
    } catch {
      /* ignore */
    }
  }
}
